export const ORGANIZATION_TABS = [
  'overview',
  'settings',
  'pricing-tier',
  'users',
  'roles',
  'features',
  'partners',
  'migrations',
  'activity',
  'login-as-audit',
  'reports',
  'hubspot-data'
] as const

export type OrganizationTab = (typeof ORGANIZATION_TABS)[number]

export const DEFAULT_TAB: OrganizationTab = 'overview'

export const ORGANIZATION_REPORTS = ['usage', 'journeys'] as const

export type OrganizationReport = (typeof ORGANIZATION_REPORTS)[number]

export const DEFAULT_REPORT: OrganizationReport = 'usage'

/** search param of the Reports tab that picks the Power BI report */
export const REPORT_PARAM = 'report'

/** the tab bar: manage tabs first, then the read-only insights */
export const ORGANIZATION_TAB_GROUPS: {
  label?: string
  tabs: { value: OrganizationTab; label: string }[]
}[] = [
  {
    tabs: [
      { value: 'overview', label: 'Overview' },
      { value: 'settings', label: 'Settings' },
      { value: 'pricing-tier', label: 'Pricing tier' },
      { value: 'users', label: 'Users' },
      { value: 'roles', label: 'Roles' },
      { value: 'features', label: 'Features' },
      { value: 'partners', label: 'Partners' },
      { value: 'migrations', label: 'Migrations' }
    ]
  },
  {
    label: 'Insights',
    tabs: [
      { value: 'activity', label: 'Activity' },
      { value: 'login-as-audit', label: 'Login as' },
      { value: 'reports', label: 'Reports' },
      { value: 'hubspot-data', label: 'HubSpot' }
    ]
  }
]

/** tab keys from before the redesign, so bookmarks and old links still open the same view */
const LEGACY_TABS: Record<
  string,
  { tab: OrganizationTab; report?: OrganizationReport }
> = {
  'usage-metrics': { tab: 'reports', report: 'usage' },
  journey_analytics: { tab: 'reports', report: 'journeys' }
}

const isTab = (value: string | null): value is OrganizationTab =>
  ORGANIZATION_TABS.includes(value as OrganizationTab)

const isReport = (value: string | null): value is OrganizationReport =>
  ORGANIZATION_REPORTS.includes(value as OrganizationReport)

/** resolves `?tab=` (and `?report=` for the Reports tab), mapping legacy tab keys */
export const resolveOrganizationTab = (
  tab: string | null,
  report: string | null
): { tab: OrganizationTab; report: OrganizationReport } => {
  const legacy = tab ? LEGACY_TABS[tab] : undefined

  if (legacy) {
    return { tab: legacy.tab, report: legacy.report ?? DEFAULT_REPORT }
  }

  return {
    tab: isTab(tab) ? tab : DEFAULT_TAB,
    report: isReport(report) ? report : DEFAULT_REPORT
  }
}
